import { LitElement, html } from "lit-element";
import { customElement, property } from "lit/decorators.js";
import { parseSpriteDescription, getClothingByPart } from "@/utils/character";
import { Color } from "excalibur";
import { loadResource } from "@/utils/resource";
import FigureThumbnail from "@/utils/figure-thumbnail";
import AssetLoader from "@/utils/asset-loader";
import "./shop-screen";
import "./navigator-screen";
import "./dialogue/profile-settings";
import "./dialogue/room-settings";
import "./dialogue/moderator-settings";
import "./build-mode";
import "./dressing-room";
import "./floating-popover-menu";

@customElement("navigation-bar")
export class Navigation extends LitElement {
  @property({ type: String }) thumbnailAvatar: any;
  assetLoader: AssetLoader;

  constructor() {
    super();

    this.assetLoader = new AssetLoader();

    this.createAvatar();
  }

  protected createRenderRoot() {
    return this;
  }

  connectedCallback() {
    super.connectedCallback();
  }

  // Button configurations
  private buttons = [
    {
      label: "Map",
      className: "is-navigator",
      action: this.onNavigator.bind(this),
    },
    {
      label: "Inventory",
      className: "is-inventory",
      action: this.onBuildMode.bind(this),
    },
    {
      label: "Shop",
      className: "is-shop",
      action: this.onShop.bind(this),
    },
    {
      label: "Friends",
      className: "is-friends",
      action: this.onShop.bind(this),
    },
  ];

  private onBuildMode() {
    const buildMode = document.querySelector("build-mode") as any;
    buildMode?.open();
  }

  private onNavigator() {
    const navigatorModal = this.querySelector("navigator-screen") as any;
    navigatorModal?.openModal();
  }

  private onShop() {
    const shopModal = this.querySelector("shop-screen") as any;
    shopModal?.openModal();
  }

  private onDressingRoom() {
    const dressingRoom = this.querySelector("dressing-room") as any;
    dressingRoom?.openModal();
  }

  private onProfileSettings() {
    const profileSettings = this.querySelector("profile-settings") as any;
    console.log(profileSettings);
    profileSettings?.openModal();
  }

  public loadAllResources = async (
    slugs: string[],
    path: string = "./public/furnitures/"
  ) => {
    // Load each resource asynchronously and ensure they all load
    const loadingPromises = slugs.map((slug) => loadResource(slug, path));

    // Wait for all resources to be loaded
    await Promise.all(loadingPromises);
  };

  private async createAvatar() {
    const userData = localStorage.getItem("tam_user");
    const look = userData ? JSON.parse(userData).look : null;
    const { skinColor } = parseSpriteDescription(look);

    const skinColorRGB = Color.fromHex(skinColor);
    skinColorRGB.a = 0.5;

    const hair = getClothingByPart(look, "hr");
    const hairColorRGB = Color.fromHex(hair.color);
    hairColorRGB.a = 0.7;

    const legs = getClothingByPart(look, "lg");
    const legsColorRGB = Color.fromHex(legs.color);
    legsColorRGB.a = 0.7;

    const torso = getClothingByPart(look, "ts");
    const torsoColorRGB = Color.fromHex(torso.color);
    torsoColorRGB.a = 0.7;

    const resources = [
      { path: "./public/figures/body/", slug: "head", assetId: "head" },
      { path: "./public/figures/body/", slug: "body", assetId: "body" },
      { path: "./public/figures/hair/", slug: hair.type, assetId: hair.type },
      { path: "./public/figures/legs/", slug: legs.type, assetId: legs.type },
      {
        path: "./public/figures/torso/",
        slug: torso.type,
        assetId: torso.type,
      },
    ];

    try {
      // Load all assets
      await this.assetLoader.loadMultipleAsepriteResources(resources);
    } catch (error) {
      console.error("Error loading assets:", error);
    }

    const action = "down";
    const animation = "idle_" + action;

    const thumb = new FigureThumbnail();
    this.thumbnailAvatar = await thumb.createMultipleThumbnails({
      head: {
        x: 17,
        y: 0,
        animationName: action,
        color: skinColorRGB.toString(),
      },
      [hair.type]: {
        x: 17,
        y: 0,
        animationName: action,
        color: hairColorRGB.toString(),
      },
      body: {
        x: 0,
        y: -65,
        animationName: animation,
        color: skinColorRGB.toString(),
      },
      [torso.type]: {
        x: 0,
        y: -65,
        animationName: animation,
        color: torsoColorRGB.toString(),
      },
    });
  }

  handleLogout() {
    fetch(`${process.env.BASE_API_URL}/logout`, {
      method: "POST",
      credentials: "include",
      body: JSON.stringify({}),
    })
      .then(() => {
        window.location.href = "/";
      })
      .catch((error) => {
        console.error("Error during logout:", error);
      });
  }

  render() {
    return html`
      <room-access-screen></room-access-screen>
      <shop-screen></shop-screen>
      <navigator-screen></navigator-screen>
      <dressing-room></dressing-room>
      <room-settings></room-settings>
      <profile-settings></profile-settings>
      <moderator-settings></moderator-settings>

      <div class="navigation">
        <div style="display: flex; gap: 20px; align-itms: center;">
          <floating-popover-menu
            .items=${[
              {
                label: "Dressing room",
                onClick: this.onDressingRoom.bind(this),
              },
              {
                label: "Settings",
                onClick: this.onProfileSettings.bind(this),
              },
              {
                label: "Logout",
                onClick: this.handleLogout,
              },
            ]}
            menuPosition="top"
            .triggerElement="${document.querySelector("#avatar")}"
          >
            <div
              id="avatar"
              slot="trigger"
              style="position: relative; top: -15px; width: 50px; height: 60px; overflow: hidden; cursor: pointer;"
            >
              <div style="transform: translateY(10px);">
                ${this.thumbnailAvatar}
              </div>
            </div>
          </floating-popover-menu>

          <div id="chat" style="display: flex; gap: 20px;"></div>
        </div>
        <div class="navigation__items">
          ${this.buttons.map(
            (button) => html`
              <div class="navigation__item">
                <button
                  class="navigation__icon ${button.className || ""}"
                  @click="${button.action}"
                  aria-label="${button.label}"
                >
                  ${button.className
                    ? html`<span>${button.label}</span>`
                    : button.label}
                </button>
              </div>
            `
          )}
        </div>
      </div>
    `;
  }
}
